/**
 * A Home, montada só com o que existe.
 *
 * Três fontes reais, nenhuma outra:
 *
 *   1. `/trips/:id/days`   — quantos dias já têm programação, e qual é o de hoje;
 *   2. `/days/:dayId`      — as atividades de hoje, para o card "agora";
 *   3. `/trips/:id/finance/summary` — quanto eu gastei e meu saldo;
 *   4. os membros da viagem, que o `TripContext` já carrega.
 *
 * O que nenhuma delas responde não é preenchido: clima, hospedagem, fotos do
 * dia e itens de compras ficam de fora até existirem de verdade. Um card com
 * número inventado passa por informação, e informação errada numa viagem é
 * pior do que um espaço vazio com um botão.
 *
 * Falha de rede não é vazio. Vazio é "não há nada"; falha é "não sei" — e as
 * duas produzem telas diferentes.
 */

import { type CalendarDate, compareCalendarDates, resolveToday, type TripDates } from '@nv/core';
import { useQuery } from '@tanstack/react-query';

import { readSummary } from '../financeiro/api';
import { useTrip } from '../../trip/TripContext';
import { useDiasDaViagem } from '../../trip/dias';
import type { AtividadeDeAgora, HomeData } from './data';

export interface AtividadeDoDia {
  id: string;
  title: string;
  startTime: string;
  /** Fim e "dia inteiro" (PO, 22/09/2026); ausentes nas atividades antigas. */
  endTime?: string | null;
  allDay?: boolean;
  locationName: string | null;
  status: string;
  sortOrder: number;
  deletedAt: string | null;
  coverThumbUrl?: string | null;
}

async function lerDia(dayId: string): Promise<{ activities: AtividadeDoDia[] }> {
  const res = await fetch(`/api/v1/days/${dayId}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar o dia (${res.status})`);
  return (await res.json()) as { activities: AtividadeDoDia[] };
}

export interface HomeState {
  data: HomeData;
  carregando: boolean;
  /** A rede falhou e não há como saber. Diferente de "não há nada". */
  erro: boolean;
  /** Data civil de hoje na timezone da viagem, ou `null` se indeterminável. */
  hoje: CalendarDate | null;
}

export function useHomeData(trip: TripDates | null, agora: Date): HomeState {
  const { tripId, currency, members } = useTrip();
  const dias = useDiasDaViagem(tripId);

  const hoje = (() => {
    if (!trip) return null;
    try {
      return resolveToday(trip.timeZone, agora, trip.utcOffsetSchedule);
    } catch {
      return null;
    }
  })();

  const diaDeHoje = hoje ? dias.days?.find((d) => d.date === hoje) : undefined;

  // Só busca as atividades quando hoje está DENTRO da viagem e o dia tem
  // alguma. Antes e depois da viagem não existe "agora".
  const atividades = useQuery({
    queryKey: ['viagem', 'dia', diaDeHoje?.id ?? ''],
    queryFn: () => lerDia(diaDeHoje?.id ?? ''),
    enabled: Boolean(diaDeHoje) && (diaDeHoje?.activityCount ?? 0) > 0,
  });

  // Antes da viagem: as atividades do 1º dia dão a hora da partida da
  // contagem regressiva (PO, 12/10/2026 — embarque às 22:15 do 1º dia).
  const primeiroDia = trip ? dias.days?.find((d) => d.date === trip.startDate) : undefined;
  const antesDaViagem = Boolean(trip && hoje && compareCalendarDates(hoje, trip.startDate) < 0);
  const doPrimeiroDia = useQuery({
    queryKey: ['viagem', 'dia', primeiroDia?.id ?? ''],
    queryFn: () => lerDia(primeiroDia?.id ?? ''),
    enabled: antesDaViagem && Boolean(primeiroDia) && (primeiroDia?.activityCount ?? 0) > 0,
  });

  const resumo = useQuery({
    queryKey: ['financeiro', 'resumo', tripId, currency],
    queryFn: () => readSummary(tripId),
    enabled: tripId.length > 0,
  });

  const lista = dias.days ?? [];
  const daysPlanned = lista.filter((d) => d.activityCount > 0).length;
  const totalDays = lista.length;

  const now = atividades.data ? escolherAgora(atividades.data.activities, agora, trip) : null;
  const data: HomeData = {
    preparation: {
      itineraryPercent: totalDays === 0 ? 0 : Math.round((daysPlanned / totalDays) * 100),
      daysPlanned,
      totalDays,
      confirmedPeople: members.length,
    },
    now,
    atividadesDeHoje: atividades.data ? atividadesDoDia(atividades.data.activities) : [],
    progresso: atividades.data ? progressoDoDia(atividades.data.activities) : null,
    minutosAteProxima: minutosAteAgora(now, agora, trip),
    horaDaPartida: doPrimeiroDia.data ? horaDaPartida(doPrimeiroDia.data.activities) : null,
    finance: resumo.data
      ? {
          myTotalMinor: resumo.data.myShareMinor,
          balanceMinor: resumo.data.myBalanceMinor,
          groupTotalMinor: resumo.data.groupTotalMinor,
        }
      : null,
  };

  return {
    data,
    carregando: dias.carregando,
    // Os dias são a espinha da tela. Financeiro fora do ar deixa um card sem
    // valor, não a Home inteira sem sentido.
    erro: dias.erro,
    hoje,
  };
}

/**
 * Qual atividade é "agora".
 *
 * A que já começou e está mais próxima do relógio; se nenhuma começou, a
 * próxima do dia. Canceladas não contam — uma atividade cancelada em destaque
 * mandaria o grupo para um lugar onde ninguém está. **Concluída também não**
 * (Home v2, 19/09): o que já foi feito nunca é "agora"; quando tudo o que
 * começou está concluído, "agora" é a próxima — e sem próxima, não há agora.
 */
export function escolherAgora(
  atividades: readonly AtividadeDoDia[],
  agora: Date,
  trip: TripDates | null,
): AtividadeDeAgora | null {
  const vivas = atividades
    .filter((a) => a.deletedAt === null && a.status !== 'cancelada' && a.status !== 'concluida')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (vivas.length === 0) return null;

  const minutosAgora = minutosLocais(agora, trip);
  // "Dia inteiro" não disputa o "acontecendo agora": não tem hora para começar
  // nem para acabar (PO, 22/09/2026).
  const comHora = vivas.filter((a) => a.allDay !== true);
  const jaComecaram = comHora.filter((a) => minutosAgora !== null && emMinutos(a.startTime) <= minutosAgora);
  // Com término preenchido, a atividade sai do "agora" quando ele passa — a
  // Home mostra a próxima, em vez de anunciar o que já acabou.
  const emAndamento = jaComecaram.filter((a) => !a.endTime || minutosAgora === null || emMinutos(a.endTime) > minutosAgora);
  const proxima = comHora.find((a) => minutosAgora !== null && emMinutos(a.startTime) > minutosAgora);

  const escolhida = emAndamento.at(-1) ?? proxima ?? vivas[0];
  if (!escolhida) return null;

  return {
    id: escolhida.id,
    time: escolhida.allDay ? 'Dia inteiro' : escolhida.endTime ? `${escolhida.startTime} – ${escolhida.endTime}` : escolhida.startTime,
    title: escolhida.title,
    location: escolhida.locationName,
    emCurso: emAndamento.length > 0 && emAndamento.at(-1)?.id === escolhida.id,
    coverThumbUrl: escolhida.coverThumbUrl ?? null,
  };
}

/**
 * A hora da partida: a 1ª atividade com horário do 1º dia (a mais cedo entre
 * as vivas; "dia inteiro" não tem hora). Sem nenhuma, `null` — a contagem
 * fica na meia-noite do 1º dia, como antes.
 */
export function horaDaPartida(atividades: readonly AtividadeDoDia[]): string | null {
  const horas = atividades
    .filter((a) => a.deletedAt === null && a.status !== 'cancelada' && !a.allDay && /^\d{2}:\d{2}$/.test(a.startTime))
    .map((a) => a.startTime)
    .sort();
  return horas[0] ?? null;
}

/** As atividades vivas de hoje, na ordem do dia — os chips da Home v2. */
export function atividadesDoDia(atividades: readonly AtividadeDoDia[]): AtividadeDoDia[] {
  return atividades.filter((a) => a.deletedAt === null && a.status !== 'cancelada').sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * "1 de 3 atividades concluídas" — o progresso do dia no card de hoje (Home,
 * lote de aparência 2). Conta só as vivas; sem nenhuma, não há o que mostrar.
 */
export function progressoDoDia(atividades: readonly AtividadeDoDia[]): { concluidas: number; total: number } | null {
  const vivas = atividades.filter((a) => a.deletedAt === null && a.status !== 'cancelada');
  if (vivas.length === 0) return null;
  return { concluidas: vivas.filter((a) => a.status === 'concluida').length, total: vivas.length };
}

/**
 * Minutos até a próxima atividade, na hora da viagem — para o alerta "Em 25
 * min: Castelo" da Home. `null` quando já começou, sem viagem ou sem "agora".
 */
export function minutosAteAgora(agora: AtividadeDeAgora | null, instante: Date, trip: TripDates | null): number | null {
  if (!agora || agora.emCurso) return null;
  const minutosAgora = minutosLocais(instante, trip);
  if (minutosAgora === null) return null;
  const faltam = emMinutos(agora.time) - minutosAgora;
  return faltam >= 0 ? faltam : null;
}

function emMinutos(hhmm: string): number {
  const [h = '0', m = '0'] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

/** Hora do dia na timezone da VIAGEM, não a do aparelho. */
function minutosLocais(agora: Date, trip: TripDates | null): number | null {
  if (!trip) return null;
  try {
    const partes = new Intl.DateTimeFormat('pt-BR', {
      timeZone: trip.timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(agora);

    const hora = partes.find((p) => p.type === 'hour')?.value;
    const minuto = partes.find((p) => p.type === 'minute')?.value;
    if (hora === undefined || minuto === undefined) return null;
    return Number(hora) * 60 + Number(minuto);
  } catch {
    return null;
  }
}
